import OrderDetail from '@Orders/OrderDetailsPage'
import JobRequestList from '@OpsQuality/JobRequestList'
import JobRequestDetails from '@OpsQuality/JobRequestDetails'
import ItemNotFound from '@Builder/QuoteDetailPage/ItemNotFound'

import { CustomRouteOption } from '~/apps/routes/RoutesTypes'
import { suspend } from '~/apps/routes/utils'
import {
    ADMIN_PATH,
    OPERATIONS_PATH,
    OPS_PRODUCTION_PATH,
    OPS_QUALITY_PATH,
    QMR_PATH,
    QUOTES_PATH,
    TEAM_PATH,
} from '~/redux-modules/preferences'
import { getenv } from '~/settings'

const OrdersPage = suspend(() => import('../../consumer/pages/Orders'))

const MyAccountPage = suspend(() => import('@User/MyAccountPage'))
const CreateTestUserPage = suspend(
    () => import('../../consumer/pages/eng/CreateTestUserPage'),
)
const ViewerDemoPage = suspend(
    () => import('../../consumer/pages/eng/ViewerDemoPage'),
)
const QuoteDetailPage = suspend(
    () => import('@Builder/QuoteDetailPage/QuoteDetailPageWrapper'),
)
const QuoteDetailFilesContent = suspend(
    () => import('@Builder/QuoteDetailPage/QuoteDetailFilesContent'),
)

const QuoteDetailCltContent = suspend(
    () => import('@Builder/QuoteDetailPage/QuoteDetailCltContent'),
)

const QuoteModelHistoryPage = suspend(
    () => import('@Builder/QuoteModelHistoryPage'),
)
const QuoteCheckoutPage = suspend(() => import('@Builder/QuoteCheckoutPage'))
const AcceptInvitePage = suspend(
    () => import('../../consumer/pages/shared/AcceptInvitePage'),
)
const QuotesHomePage = suspend(() => import('@Builder/QuotesHomePage'))
const AcceptTermsPage = suspend(() => import('@Builder/AcceptTermsPage'))
const QuotesDropzonePage = suspend(() => import('@Builder/QuotesDropzonePage'))
const MaintenancePage = suspend(
    () => import('../../consumer/pages/shared/MaintenancePage'),
)
const RegionalWorkbenchRoute = suspend(() =>
    Promise.resolve({
        default: () => {
            window.location.href = `${getenv('WB_DOMAIN')}/login?return_to=${
                window.location.pathname
            }`

            return null
        },
    }),
)
const OperationsWorkbenchQuality = suspend(() => import('@OpsQuality/route'))
const ProductionWorkbench = suspend(() => import('@ProdWb/index'))
const ProdWorkbenchExceptionFailureDetails = suspend(
    () => import('@ProdWb/ExceptionFailureDetails'),
)
const ProdWorkbenchCarDetails = suspend(() => import('@ProdWb/CarDetails'))
const MfgPartner = suspend(() => import('@MfgPartner/route'))
const MfgPartnerList = suspend(() => import('@MfgPartner/MfgPartnerList'))
const MfgPartnerDetails = suspend(() => import('@MfgPartner/MfgPartnerDetails'))
const MfgPartnerPerformanceDetails = suspend(
    () => import('@MfgPartner/MfgPartnerPerformanceDetails'),
)

const InternalToolsPage = suspend(() => import('@Admin/InternalToolsPage'))
const EstimateItemZipPage = suspend(
    () => import('@Admin/EstimateItems/EstimateItemZipPage'),
)
const JobBoardPage = suspend(() => import('@Admin/JobBoardPage'))
const AdminQuoteDetailsPage = suspend(
    () => import('@Admin/AdminQuoteDetailsPage'),
)
const AdminToolingQuotingPage = suspend(
    () => import('@Admin/AdminToolingQuotingPage'),
)
const QuoteModelRevisionsQueuePage = suspend(
    () => import('@Admin/QuoteModelRevisionsQueuePage'),
)
const EstimateItemBubbleDrawingPage = suspend(
    () => import('@Admin/BubbleDrawingPage/EstimateItemBubbleDrawingPage'),
)
const QuoteModelRevisionBubbleDrawingPage = suspend(
    () =>
        import('@Admin/BubbleDrawingPage/QuoteModelRevisionBubbleDrawingPage'),
)
const QMRGenerateDrawingEndpoint = suspend(
    () => import('@Admin/DrawingGeneratorPage/QMRGenerateDrawingEndpoint'),
)
const AdminQuotingPage = suspend(() => import('@Admin/AdminQuotingPage'))
const GenericSearchPage = suspend(() => import('@Admin/GenericSearchPage'))
const AdminFeatureFlagDashboardPage = suspend(
    () => import('@Admin/AdminFeatureFlagDashboardPage'),
)
const AdminUserProfilePage = suspend(
    () => import('@Admin/AdminUserProfilePage'),
)
const AdminSearchPage = suspend(() => import('@Admin/AdminSearchPage'))
const ShippingCalculatorPage = suspend(
    () => import('@Admin/ShippingCalculatorPage'),
)
const SecureProjectsHomePage = suspend(
    () => import('@Admin/SecureProjectsHomePage'),
)
const DfmMessagePage = suspend(() => import('@Admin/DfmMessageManagementPage'))
const SecureProjectDetailPage = suspend(
    () => import('@Admin/SecureProjectDetailPage'),
)
const AdminUserQuoteDetailPage = suspend(
    () => import('@Admin/AdminUserQuoteDetailPage'),
)
const AdminUserQuoteModelHistoryPage = suspend(
    () => import('@Admin/AdminUserQuoteModelHistoryPage'),
)
const AdminUserQuoteCheckoutPage = suspend(
    () => import('@Admin/AdminUserQuoteCheckoutPage'),
)
const AdminUserOrderDetailPage = suspend(
    () => import('@Admin/AdminUserOrderDetailPage'),
)
const MarkupAdjustmentPage = suspend(
    () => import('@Admin/MarkupAdjustmentPage'),
)
const AdminUserQuotesDropzonePage = suspend(
    () => import('@Admin/AdminUserQuotesDropzonePage'),
)
const AdminPlatformOrderSearchPage = suspend(
    () => import('@Admin/AdminPlatformOrderSearchPage'),
)
const AdminPlatformOrderDetailPage = suspend(
    () => import('@Admin/AdminPlatformOrderDetailPage'),
)
const BusinessAccountDetailsPage = suspend(
    () => import('@Admin/BusinessAccountDetailsPage'),
)
const ForbiddenPage = suspend(
    () => import('../../consumer/pages/shared/ForbiddenPage/ForbiddenPage'),
)
const ForbiddenQuotePage = suspend(
    () =>
        import('../../consumer/pages/shared/ForbiddenPage/ForibddenQuotePage'),
)
const TeamWorkspacesPage = suspend(
    () => import('@Teams/TeamWorkspacesPage/TeamWorkspacesPage'),
)
const TeamDetailsPage = suspend(
    () => import('@Teams/TeamDetailsPage/TeamDetailsPage'),
)
const TeamQuoteDetailsPage = suspend(
    () => import('@Teams/TeamQuoteDetailsPage'),
)

const TeamQuotesDropzonePage = suspend(
    () => import('@Teams/TeamQuotesDropzonePage'),
)
const TeamQuoteCheckoutPage = suspend(
    () => import('@Teams/TeamQuoteCheckoutPage'),
)
const TeamOrderDetailsPage = suspend(
    () => import('@Teams/TeamOrderDetailsPage'),
)
const AdminTeamDetailsPage = suspend(
    () => import('@Teams/Admin/AdminTeamDetailsPage'),
)
const AdminTeamUserQuoteDetailPage = suspend(
    () => import('@Teams/Admin/AdminTeamUserQuoteDetailPage'),
)
const AdminTeamUserOrderDetailPage = suspend(
    () => import('@Teams/Admin/AdminTeamUserOrderDetailPage'),
)
const AdminTeamWorkspacesPage = suspend(
    () => import('@Teams/Admin/AdminTeamWorkspacesPage'),
)
const AdminUserQuotesPage = suspend(() => import('@Admin/AdminUserQuotesPage'))

const quoteDetailPageCommonSubRoutes: CustomRouteOption[] = [
    {
        path: [
            `optimizations`,
            `optimizations/part`,
            `optimizations/part/:revisionId`,
            `optimizations/part/:revisionId/:tabs`,
            `optimizations/part/:revisionId/:tabs/:perspective`,
        ],
        element: <QuoteDetailCltContent />,
    },
    {
        index: true,
        element: <QuoteDetailFilesContent />,
    },
    {
        path: '*',
        element: <ItemNotFound isQuote={true} />,
    },
]

/**
 * List of private(hidden under login) route configs to be used by react-router
 * When this config will be mapped to react-router <Route /> props each element will be wrapped with custom <PrivateRoute /> component
 */
export const privateRoutes: CustomRouteOption[] = [
    {
        path: ['/pages/my-account', '/pages/my-account/:tab'],
        element: <MyAccountPage />,
        privateRoute: true,
    },
    {
        path: '/pages/create-test-user/:email',
        element: <CreateTestUserPage />,
        privateRoute: true,
    },
    {
        path: '/pages/quote-model-revisions/:id/viewer-demo',
        element: <ViewerDemoPage />,
        privateRoute: true,
    },
    {
        path: [`${QUOTES_PATH}/:quoteId/*`],
        element: <QuoteDetailPage />,
        privateRoute: true,
        children: [
            ...quoteDetailPageCommonSubRoutes,
            {
                path: [
                    `:tabs`,
                    `qmr`,
                    `qmr/:qmrId`,
                    `qmr/:qmrId/:tabs`,
                    `part`,
                    `part/:revisionId`,
                    `part/:revisionId/:tabs`,
                    `part/:revisionId/:tabs/:perspective`,
                ],
                element: <QuoteDetailFilesContent />,
            },
        ],
    },
    {
        path: [
            '/pages/quote-model-revisions/:revisionId/viewer',
            '/pages/quote-model-revisions/:revisionId/viewer/:sidebarId',
        ],
        element: <QuoteDetailPage />,
        privateRoute: true,
        getQuoteId: true,
    },
    {
        path: [
            `${QMR_PATH}/:qmrId/history`,
            '/admin/quote-model-revision/:qmrId/history',
        ],
        element: <QuoteModelHistoryPage />,
        privateRoute: true,
    },
    {
        path: ['/pages/orders/quote', '/pages/orders/quote/:quoteId'],
        element: <QuoteCheckoutPage />,
        privateRoute: true,
    },
    {
        path: [
            '/pages/quotes/:id/accept-invite',
            '/pages/quotes/:id/accept-open-invite',
        ],
        element: <AcceptInvitePage />,
        privateRoute: true,
    },
    {
        path: '/pages/quotes',
        element: <QuotesHomePage />,
        privateRoute: true,
    },
    {
        path: '/pages/accept-terms',
        element: <AcceptTermsPage />,
        privateRoute: true,
    },
    {
        path: '/pages/quotes/upload',
        element: <QuotesDropzonePage />,
        privateRoute: true,
    },
    {
        path: '/pages/maintenance',
        element: <MaintenancePage />,
        privateRoute: true,
    },
    {
        path: '/workbench/*',
        element: <RegionalWorkbenchRoute />,
        privateRoute: true,
    },
    {
        path: OPS_QUALITY_PATH,
        element: <OperationsWorkbenchQuality />,
        privateRoute: true,
        verifyAdmin: true,
        children: [
            {
                index: true,
                element: <JobRequestList />,
            },
            {
                path: ':jobRequestId/:tab',
                element: <JobRequestDetails />,
            },
        ],
    },
    {
        path: [
            `${OPERATIONS_PATH}/production`,
            `${OPERATIONS_PATH}/production/:tab`,
        ],
        element: <ProductionWorkbench />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: [
            `${OPERATIONS_PATH}/production/exception-details/:name`,
            `${OPERATIONS_PATH}/production/exception-details/:name/:tab/*`,
        ],
        element: <ProdWorkbenchExceptionFailureDetails />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: [
            `${OPS_PRODUCTION_PATH}/car/:id`,
            `${OPS_PRODUCTION_PATH}/car/:id/:tab/*`,
        ],
        element: <ProdWorkbenchCarDetails />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${OPERATIONS_PATH}/mfgPartners/*`,
        element: <MfgPartner />,
        privateRoute: true,
        verifyAdmin: true,
        children: [
            {
                index: true,
                element: <MfgPartnerList />,
            },
            {
                path: ':mfgPartnerId/:tab',
                element: <MfgPartnerDetails />,
            },
            {
                path: ':mfgPartnerId/performance/:performanceDetailId/:tab',
                element: <MfgPartnerPerformanceDetails />,
            },
        ],
    },
    {
        path: ADMIN_PATH,
        element: <InternalToolsPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/estimate_items_zip`,
        element: <EstimateItemZipPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/jobBoard`,
        element: <JobBoardPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: ['/admin/quotes/:id', '/admin/quotes/:id/projects/:projectId'],
        element: <AdminQuoteDetailsPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: '/admin/quotes/:id/mold/:moldId',
        element: <AdminToolingQuotingPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: '/admin/quote-model-revisions',
        element: <QuoteModelRevisionsQueuePage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/estimates/:estimateItemId/bubble_drawing`,
        element: <EstimateItemBubbleDrawingPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: '/admin/quote-model-revisions/:quoteModelRevisionId/bubble_drawing',
        element: <QuoteModelRevisionBubbleDrawingPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: '/admin/quote-model-revisions/:quoteModelRevisionId/generate_drawing',
        element: <QMRGenerateDrawingEndpoint />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: [
            '/admin/quote-model-revisions/:id',
            '/admin/quote-model-revisions/:id/:tabs',
            '/admin/quote-model-revisions/:id/projects/:projectId',
            '/admin/quote-model-revisions/:id/projects/:projectId/:tabs',
        ],
        element: <AdminQuotingPage />,
        privateRoute: true,
        verifyAdmin: true,
        isQmrPage: true,
    },
    {
        path: `${ADMIN_PATH}/generic-search`,
        element: <GenericSearchPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/feature-flags`,
        element: <AdminFeatureFlagDashboardPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/user/:id`,
        element: <AdminUserProfilePage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/search`,
        element: <AdminSearchPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: [
            `${ADMIN_PATH}/user/:userId/quotes`,
            `${ADMIN_PATH}/user/:userId/quotes/create-team/:businessAccountId`,
        ],
        element: <AdminUserQuotesPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: [
            `${ADMIN_PATH}/user/:userId/quotes/upload`,
            `${ADMIN_PATH}/user/:userId/team/:teamId/quotes/upload`,
        ],
        element: <AdminUserQuotesDropzonePage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: [`${ADMIN_PATH}/user/:userId/quotes/:quoteId/*`],
        element: <AdminUserQuoteDetailPage />,
        privateRoute: true,
        verifyAdmin: true,
        children: [
            ...quoteDetailPageCommonSubRoutes,
            {
                path: [
                    `:tabs`,
                    `part`,
                    `part/:revisionId`,
                    `part/:revisionId/:tabs`,
                    `part/:revisionId/:tabs\\?view=:perspective`,
                    `projects/:projectId`,
                    `projects/:projectId/:revisionId`,
                    `projects/:projectId/:revisionId/:tabs`,
                    `qmr`,
                    `qmr/:qmrId`,
                    `qmr/:qmrId/:tabs`,
                ],
                element: <QuoteDetailFilesContent />,
            },
        ],
    },
    {
        path: `${ADMIN_PATH}/user/:userId/quotes/:quoteId/checkout`,
        element: <AdminUserQuoteCheckoutPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/user/:userId/quotes/:quoteId/qmr/:qmrId/history`,
        element: <AdminUserQuoteModelHistoryPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: '/orders',
        element: <QuotesHomePage />,
        privateRoute: true,
    },
    {
        path: '/orders/:orderName',
        element: <OrdersPage />,
        privateRoute: true,
        children: [
            {
                index: true,
                element: <OrderDetail />,
            },
            {
                path: [
                    'part/:revisionId',
                    'part/:revisionId/:tabs',
                    'qmr',
                    'qmr/:qmrId',
                    'qmr/:qmrId/:tabs',
                ],
                element: <OrderDetail />,
            },
        ],
    },
    {
        path: `${ADMIN_PATH}/user/:userId/orders`,
        element: <AdminUserQuotesPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: [
            `${ADMIN_PATH}/user/:userId/orders/:orderName`,
            `${ADMIN_PATH}/user/:userId/orders/:orderName/projects/:projectId`,
            `${ADMIN_PATH}/user/:userId/orders/:orderName/part/:revisionId`,
            `${ADMIN_PATH}/user/:userId/orders/:orderName/part/:revisionId/:tabs`,
            `${ADMIN_PATH}/user/:userId/orders/:orderName/qmr`,
            `${ADMIN_PATH}/user/:userId/orders/:orderName/qmr/:qmrId`,
            `${ADMIN_PATH}/user/:userId/orders/:orderName/qmr/:qmrId/:tabs`,
        ],
        element: <AdminUserOrderDetailPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/markup-adjustment`,
        element: <MarkupAdjustmentPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/shipping-calculator`,
        element: <ShippingCalculatorPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: [`${ADMIN_PATH}/403/quote/:quoteId`],
        element: <ForbiddenQuotePage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: [`${ADMIN_PATH}/403`, `${ADMIN_PATH}/403/obo-user/:name`],
        element: <ForbiddenPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/projects`,
        element: <SecureProjectsHomePage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/dfm-message-management`,
        element: <DfmMessagePage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/projects/:id/:tab`,
        element: <SecureProjectDetailPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/orders`,
        element: <AdminPlatformOrderSearchPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/orders/:orderName`,
        element: <AdminPlatformOrderDetailPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: [
            '/admin/business-account/:businessAccountId',
            '/admin/business-account/:businessAccountId/:tab',
        ],
        element: <BusinessAccountDetailsPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${TEAM_PATH}/workspaces`,
        element: <TeamWorkspacesPage />,
        privateRoute: true,
    },
    {
        path: `${TEAM_PATH}/:teamId/quotes/upload`,
        element: <TeamQuotesDropzonePage />,
        privateRoute: true,
    },
    {
        path: [`${TEAM_PATH}/:teamId`, `${TEAM_PATH}/:teamId/:tab`],
        element: <TeamDetailsPage />,
        privateRoute: true,
    },
    {
        path: [`${TEAM_PATH}/:teamId/quotes/:quoteId/*`],
        element: <TeamQuoteDetailsPage />,
        privateRoute: true,
        children: [
            ...quoteDetailPageCommonSubRoutes,
            {
                path: [
                    `:tabs`,
                    `part/:revisionId`,
                    `part/:revisionId/:tabs`,
                    `part/:revisionId/:tabs/:perspective`,
                ],
                element: <QuoteDetailFilesContent />,
            },
        ],
    },
    {
        path: `${TEAM_PATH}/:teamId/quotes/:quoteId/checkout`,
        element: <TeamQuoteCheckoutPage />,
        privateRoute: true,
    },
    {
        path: `${TEAM_PATH}/:teamId/orders/:orderName`,
        element: <TeamOrderDetailsPage />,
        privateRoute: true,
        children: [
            {
                index: true,
                element: <OrderDetail isLocatedInTeamPage />,
            },
            {
                path: [
                    'part/:revisionId',
                    'part/:revisionId/:tabs',
                    'qmr',
                    'qmr/:qmrId',
                    'qmr/:qmrId/:tabs',
                ],
                element: <OrderDetail isLocatedInTeamPage />,
            },
        ],
    },
    {
        path: `${ADMIN_PATH}/user/:userId/team/workspaces`,
        element: <AdminTeamWorkspacesPage />,
        privateRoute: true,
    },
    {
        path: [
            `${ADMIN_PATH}/user/:userId/team/:teamId`,
            `${ADMIN_PATH}/user/:userId/team/:teamId/:tab`,
        ],
        element: <AdminTeamDetailsPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
    {
        path: `${ADMIN_PATH}/user/:userId/team/:teamId/quotes/:quoteId/*`,
        element: <AdminTeamUserQuoteDetailPage />,
        privateRoute: true,
        verifyAdmin: true,
        children: [
            ...quoteDetailPageCommonSubRoutes,
            {
                path: [
                    `:tabs`,
                    `qmr`,
                    `qmr/:revisionId`,
                    `qmr/:revisionId/:tabs`,
                    `part`,
                    `part/:revisionId`,
                    `part/:revisionId/:tabs`,
                ],
                element: <QuoteDetailFilesContent />,
            },
        ],
    },
    {
        path: `${ADMIN_PATH}/user/:userId/team/:teamId/orders/:orderName`,
        element: <AdminTeamUserOrderDetailPage />,
        privateRoute: true,
        verifyAdmin: true,
    },
]
